import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withHashLocation } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { CookieService } from 'ngx-cookie-service';
import {
  MSAL_INSTANCE,
  MsalBroadcastService,
  MsalService,
} from '@azure/msal-angular';
import { PublicClientApplication, type IPublicClientApplication } from '@azure/msal-browser';

import { routes } from './app.routes';
import { jwtInterceptor } from './interceptors/jwt.interceptor';
import { buildMsalConfiguration } from './services/msal-config';
import { WorkIqService } from './services/work-iq.service';
import { environment } from '../environments/environment';

export function msalInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(buildMsalConfiguration());
}

/** PAT-335 — initialize MSAL and complete redirect → Orchestrator connect when workIQ is enabled. */
const workIqMsalProviders = environment.workIqEnabled
  ? [
      { provide: MSAL_INSTANCE, useFactory: msalInstanceFactory },
      MsalService,
      MsalBroadcastService,
      provideAppInitializer(() => {
        const msal = inject(MsalService);
        const workIq = inject(WorkIqService);
        return (async () => {
          await msal.instance.initialize();
          const result = await msal.instance.handleRedirectPromise();
          if (result?.account) {
            try {
              await workIq.recordConnectFromRedirectResult(result);
            } catch {
              // PAT-359: keep app boot non-blocking but surface reconnect need to user.
              workIq.markReconnectRequired();
            }
          }
        })();
      }),
    ]
  : [];

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withHashLocation()),
    provideHttpClient(withInterceptors([jwtInterceptor])),
    provideIonicAngular({ mode: 'ios' }),
    CookieService,
    ...workIqMsalProviders,
  ],
};
